/**
 * WP-V2-PK — lokalne animacje placu zabaw (huśtawka / zjeżdżalnia / babki / leżak).
 * Zero sync do ParkState — tylko klient.
 * WAVE7-C: plany jazdy dopasowane do kotwic bg_park + fizyczne leżenie na ławce.
 */
import {
  PARK_SLIDE_DEF_ID,
  PARK_SWING_DEF_ID,
  PARK_BENCH_DEF_ID,
  sandcakePlacement,
  MAX_LOCAL_SANDCAKES,
} from './objects.js';

export type RideKind = 'swing' | 'slide';

export type RidePlan = {
  kind: RideKind;
  /** Punkt wsiadania (góra zjeżdżalni / siedzisko). */
  mount: { x: number; y: number };
  /** Punkt zejścia. */
  dismount: { x: number; y: number };
  /** Czas animacji jazdy (ms). */
  durationMs: number;
};

/** Plan leżenia na ławce (Toca: piesek leży NA ławce, nie ikona obok). */
export type LoungePlan = {
  kind: 'lounge';
  seat: { x: number; y: number };
  durationMs: number;
};

export function rideKindFromDef(defId: string): RideKind | null {
  if (defId === PARK_SWING_DEF_ID) return 'swing';
  if (defId === PARK_SLIDE_DEF_ID) return 'slide';
  return null;
}

export function isBenchDef(defId: string): boolean {
  return defId === PARK_BENCH_DEF_ID;
}

/**
 * Plan jazdy wokół pozycji obiektu (placement / kotwica tła).
 * Huśtawka: siedzisko buja w poziomie; zjeżdżalnia: góra → dół w skos.
 */
export function planRide(
  kind: RideKind,
  objectX: number,
  objectY: number,
): RidePlan {
  if (kind === 'swing') {
    return {
      kind: 'swing',
      // siedzisko niżej niż belka
      mount: { x: objectX, y: objectY + 36 },
      dismount: { x: objectX + 48, y: objectY + 50 },
      durationMs: 3000,
    };
  }
  // slide — góra platformy → dół zjazdu (w prawo-dół jak na bg_park)
  return {
    kind: 'slide',
    mount: { x: objectX - 20, y: objectY - 48 },
    dismount: { x: objectX + 70, y: objectY + 80 },
    durationMs: 1100,
  };
}

/** Piesek siada/leży dokładnie na ławce. */
export function planLounge(objectX: number, objectY: number): LoungePlan {
  return {
    kind: 'lounge',
    seat: { x: objectX, y: objectY + 8 },
    durationMs: 2800,
  };
}

/**
 * Pozycja na huśtawce w t ∈ [0,1] — sinus w poziomie, lekki łuk w pionie.
 */
export function swingPose(
  t: number,
  base: { x: number; y: number },
  amplitude = 42,
): { x: number; y: number; angle: number } {
  const progress = Math.min(1, Math.max(0, t));
  if (progress === 0 || progress === 1) {
    return { x: base.x, y: base.y, angle: 0 };
  }
  const phase = Math.sin(progress * Math.PI * 2);
  // Obwiednia wygasza prędkość na starcie/końcu; wahadło unosi się na skrajach.
  const envelope = Math.pow(Math.sin(progress * Math.PI), 0.55);
  const x = base.x + phase * amplitude * envelope;
  const y = base.y - Math.abs(phase) * 10 * envelope;
  const angle = phase * 0.38 * envelope;
  return { x, y, angle };
}

/**
 * Pozycja na zjeżdżalni t ∈ [0,1] — liniowy zjazd góra→dół + lekka parabola.
 */
export function slidePose(
  t: number,
  mount: { x: number; y: number },
  dismount: { x: number; y: number },
): { x: number; y: number } {
  const u = Math.min(1, Math.max(0, t));
  // Smootherstep: zerowa prędkość i przyspieszenie na wejściu oraz lądowaniu.
  const ease = u * u * u * (u * (u * 6 - 15) + 10);
  return {
    x: mount.x + (dismount.x - mount.x) * ease,
    y: mount.y + (dismount.y - mount.y) * ease,
  };
}

export type SandcakeState = {
  count: number;
  placements: ReturnType<typeof sandcakePlacement>[];
};

export function createSandcakeState(): SandcakeState {
  return { count: 0, placements: [] };
}

/** Dodaj lokalną babkę; max MAX_LOCAL_SANDCAKES. */
export function addSandcake(state: SandcakeState): SandcakeState {
  if (state.count >= MAX_LOCAL_SANDCAKES) return state;
  const placement = sandcakePlacement(state.count);
  return {
    count: state.count + 1,
    placements: [...state.placements, placement],
  };
}

export { MAX_LOCAL_SANDCAKES };
